import React from "react";
import { Tooltip } from "antd";
import moment from "moment";
import { useSelector } from "react-redux";
import { Folder, Clock, Calendar } from "lucide-react"; // Lucide icons
import { dentalIcons } from "../../dentail-indetifcation/utils-icons/dentalIcons";

// Helpers
const fmtExact = (dt) =>
  dt
    ? moment
        .utc(dt, "YYYY-MM-DD HH:mm:ss")
        .local()
        .format("MMM D, YYYY • hh:mm A")
    : "—";
const fromNow = (dt) =>
  dt ? moment.utc(dt, "YYYY-MM-DD HH:mm:ss").local().fromNow() : "";

// Small, clean initials
const Initials = ({ name }) => {
  const t = (name || "")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="h-7 w-7 rounded-full bg-[#20C3FF1A] text-[#20C3FF] flex items-center justify-center text-[11px] font-semibold">
      {t || "PE"}
    </div>
  );
};

// Small buttons (consistent sizing)
const Btn = ({ variant = "primary", children, onClick, ariaLabel }) => {
  const base = "h-[34px] px-3 text-[13px] rounded-md transition-colors";
  const styles = {
    primary:
      "bg-[#20c3ff] text-white hover:bg-[#148BE6] active:bg-[#0F79C8] shadow-sm",
    secondary:
      "border border-[#E2E8F0] text-[#374151] bg-white hover:bg-[#F8FAFC]",
    danger:
      "border border-[#FEE2E2] text-[#DC2626] bg-white hover:bg-[#FEF2F2]",
  }[variant];

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={`${base} ${styles}`}
    >
      {children}
    </button>
  );
};

const SingleProjectItem = ({ item, onClick, onEdit, onDelete }) => {
  const me = useSelector((s) => s.settings_data.profileSettings_data);
  const createdAt = item?.created_at;
  const updatedAt = item?.updated_at || createdAt;
  const ownerName = item?.owner?.name || "Unknown";
  const isOwner = item?.owner?.id === me?.id;

  const thumbSrc = item?.thumbnail
    ? `${item.thumbnail}?v=${item.updated_at || item.created_at || Date.now()}`
    : dentalIcons.toothPlaceholder;

  return (
    <div className="w-full bg-white  rounded-[12px] shadow-[0_1px_8px_rgba(0,0,0,0.05)] hover:shadow-[0_3px_14px_rgba(0,0,0,0.08)] transition-all duration-200 mb-3">
      <div className="flex items-start justify-between gap-4 px-3.5 py-3.5">
        {/* LEFT: icon, title, owner, created-ago */}
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-[100px] w-[100px] rounded-[12px]  bg-[#F5F7FA] flex items-center justify-center shrink-0  overflow-hidden">
            {/* <img src={dentalIcons.projectAnalysisIcon} className="h-full" /> */}
            <img
              src={thumbSrc}
              alt={item.title}
              key={thumbSrc}
              className="h-full w-full object-cover rounded-inherit"
            />
          </div>

          <div className="min-w-0">
            <div className="flex flex-col items-start">
              <div className="px-3 py-1 mb-1 bg-cyan-400/10 rounded-[32px]  inline-flex justify-center items-center gap-1 overflow-hidden">
                <Folder
                  className="h-5 w-5 text-[#2563EB]"
                  strokeWidth={1.8}
                  color="#20C3FF"
                />

                <div className="text-cyan-400 text-sm font-medium">
                  #{item.identifier}
                </div>
              </div>
              <button
                onClick={onClick}
                title={item?.title}
                className="text-[16px] font-semibold text-[#0F172A] hover:text-[#2563EB] transition-colors truncate"
              >
                {item?.title}
              </button>
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-2 text-[13px] text-[#606C80]">
              <Initials name={ownerName} />
              <span>
                by{" "}
                <span className="font-medium text-[#344256]">{ownerName}</span>
              </span>
              <span className="text-[#CBD5E1]">•</span>
              Created on{" "}
              {moment
                .utc(item?.created_at, "YYYY-MM-DD HH:mm:ss")
                .local()
                .format("MMM D, YYYY")}
            </div>
          </div>
        </div>

        {/* RIGHT: meta row + buttons (compact) */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          {/* Meta row (Updated + Created, right-aligned) */}
          <div className="flex items-center gap-4 text-[12px]">
            <Tooltip title={fmtExact(updatedAt)}>
              <div className="flex items-center gap-1 text-[#15803D]">
                <Clock className="h-3.5 w-3.5" strokeWidth={1.8} />
                <span className="font-medium">Updated</span>
                <span>{fromNow(updatedAt)}</span>
              </div>
            </Tooltip>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-2">
            <Btn variant="primary" onClick={onClick} ariaLabel="Open project">
              Open
            </Btn>
            <Btn variant="secondary" onClick={onEdit} ariaLabel="Edit project">
              Edit
            </Btn>
            {isOwner && (
              <Btn
                variant="danger"
                onClick={onDelete}
                ariaLabel="Delete project"
              >
                Delete
              </Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleProjectItem;
